// Single fallback data source until backend GET /insights is ready.
// The hero/status message is intentionally separate from Personal Insights and Health Tips.
//
// Five insight categories rendered in this fixed order. The dashboard
// widget shows the first two; the Insights page shows the first two
// up front and the rest reveal behind a "See more" toggle. Tapping a
// card opens a modal with the longer `description`.
//
// Field shape: existing fields (id/title/body/tone/metric/metricLabel/
// iconColor) are kept so the existing UI keeps working unchanged. New
// fields (`preview`, `description`) are layered on top:
//   - `body` keeps being the short text shown inline (kept for back-
//     compat with any consumer that hasn't been updated yet)
//   - `preview` is the same short text, used by callers that want to
//     opt in to the new naming
//   - `description` is the long copy revealed by the modal

export const DASHBOARD_INSIGHTS_PREVIEW_LIMIT = 2;
export const DASHBOARD_TIPS_PREVIEW_LIMIT = 2;
export const INSIGHTS_PAGE_INITIAL_LIMIT = 2;

// Only used by the top hero on the Insights page.
export const STATIC_RECOVERY_STATUS = {
  id: 'recovery-status',
  title: "You're Getting Better!",
  body: 'Your body is responding well to your lifestyle choices.',
};

export const FALLBACK_HEALTH_INSIGHTS = [
  {
    id: 'state-of-health',
    tone: 'green',
    title: 'State of Health',
    body: 'Your overall health markers look solid this week. Resting heart rate and HRV are both within healthy bands.',
    preview: 'Your overall health markers look solid this week. Resting heart rate and HRV are both within healthy bands.',
    description:
      'Your overall health markers look solid this week. Resting heart rate and HRV are both within healthy bands, which suggests your cardiovascular system is well-regulated. Sleep duration has been consistent and your body appears to be recovering between activities. Keep your routine steady — small day-to-day variations are normal and not a cause for concern.',
    metric: 'Stable',
    metricLabel: '',
    iconColor: '#10b981',
  },
  {
    id: 'processing-of-stress',
    tone: 'purple',
    title: 'Processing of Stress',
    body: 'Your nervous system is handling daily stress reasonably well. Brief HRV dips followed by quick recovery are a healthy pattern.',
    preview: 'Your nervous system is handling daily stress reasonably well. Brief HRV dips followed by quick recovery are a healthy pattern.',
    description:
      "Your nervous system is handling daily stress reasonably well. Brief HRV dips followed by quick recovery are a healthy pattern — they show your body can mobilise resources under load and return to baseline. Consider short breathing breaks during high-load afternoons to keep this pattern going. If you notice prolonged dips that don't recover overnight, that's the signal to scale back.",
    metric: 'Healthy',
    metricLabel: '',
    iconColor: '#6366f1',
  },
  {
    id: 'biological-age',
    tone: 'green',
    title: 'Biological Age',
    body: 'Your biological age estimate is tracking below your chronological age — a strong indicator of recovery capacity.',
    preview: 'Your biological age estimate is tracking below your chronological age — a strong indicator of recovery capacity.',
    description:
      'Your biological age estimate is tracking below your chronological age, which is a strong indicator of recovery capacity. This number is computed from HRV, sleep regularity and resting heart rate trends, so it shifts gradually rather than day-to-day. Maintaining the habits you already have — consistent sleep, regular activity, low alcohol — is what keeps this number healthy over time.',
    metric: '−3 yrs',
    metricLabel: '',
    iconColor: '#10b981',
  },
  {
    id: 'performance-potential',
    tone: 'purple',
    title: 'Performance Potential',
    body: 'You have headroom for a higher-intensity day. Recovery markers point to readiness for harder training.',
    preview: 'You have headroom for a higher-intensity day. Recovery markers point to readiness for harder training.',
    description:
      'You have headroom for a higher-intensity day. Your recovery markers — HRV, sleep, resting heart rate — all point to a body ready for harder training. This is a good window to do a longer or faster session if you have one planned. After a hard effort the same metrics will dip; that dip is expected and not a problem unless it persists for several days.',
    metric: 'Ready',
    metricLabel: '',
    iconColor: '#6366f1',
  },
  {
    id: 'burnout-resistance',
    tone: 'green',
    title: 'Burnout Resistance',
    body: 'Your stress / recovery balance over the last 14 days suggests good resilience headroom.',
    preview: 'Your stress / recovery balance over the last 14 days suggests good resilience headroom.',
    description:
      'Your stress / recovery balance over the last 14 days suggests good resilience headroom. The model looks at how often your HRV recovers fully overnight versus how often it stays suppressed — yours is mostly the former. Watch for signs the trend is reversing: chronically low HRV, poor sleep continuity, or feeling "wired but tired". Those are the early signals to act on, not the late ones.',
    metric: 'Strong',
    metricLabel: '',
    iconColor: '#10b981',
  },
];

export const FALLBACK_HEALTH_TIPS = [
  {
    id: 'prioritize-rest',
    title: 'Prioritize Rest Tonight',
    body: 'Your body needs recovery time after intense activity.',
    preview: 'Your body needs recovery time after intense activity.',
    description:
      'Your body needs recovery time after intense activity. Aim to start winding down 30 minutes earlier than usual tonight — dim lighting, lighter meal, no screens for the last 20 minutes. Recovery during sleep is when your nervous system rebalances, so a slightly longer night now pays off in HRV and energy tomorrow.',
  },
  {
    id: 'stay-hydrated',
    title: 'Stay Hydrated',
    body: 'Aim for at least 3 liters of water today.',
    preview: 'Aim for at least 3 liters of water today.',
    description:
      'Aim for at least 3 liters of water today. Mild dehydration is one of the most common reasons HRV scores look worse than they should — it raises resting heart rate and reduces parasympathetic tone. Spread your intake across the day rather than catching up in the evening, which would otherwise interrupt your sleep.',
  },
];